import { buffers } from 'redux-saga';
import { actionChannel, call, delay, race, take } from 'redux-saga/effects';

// A save and the GRANTED/REVOKED_PERMISSION signal it causes land within a second or two of each other.
export const ACCESS_REFRESH_QUIET_MS = 750;
export const ACCESS_REFRESH_MAX_WAIT_MS = 3000;

/**
 * One silent GET /access per burst of refresh requests, and never two in flight from one tab
 * (plan access-refetch row 7, INV-11).
 *
 * Every save saga and both permission socket handlers ask for a refresh. With `takeLatest` each
 * request cancelled the previous saga but not its HTTP request, so the server still built every
 * multi-megabyte response the tab then discarded.
 *
 * Invariant: every request is answered by a fetch that STARTS after it was made, so a reload asked
 * for after a write never reads from before the write. Requests made while a fetch is in flight are
 * held (one is enough) and answered by a single rerun once it finishes.
 *
 * @param {*} pattern - the action that requests a refresh
 * @param {Function} worker - saga that fetches and stores the access list
 * @param {() => number} [now] - clock, injectable for tests
 */
export function* coalesceAccessRefresh(pattern, worker, now = Date.now) {
	const requests = yield actionChannel(pattern, buffers.sliding(1));
	while (true) {
		yield take(requests);
		const firstRequestAt = now();
		while (true) {
			const remaining = ACCESS_REFRESH_MAX_WAIT_MS - (now() - firstRequestAt);
			if (remaining <= 0) break;
			const { again } = yield race({
				again: take(requests),
				quiet: delay(Math.min(ACCESS_REFRESH_QUIET_MS, remaining))
			});
			if (!again) break;
		}
		try {
			yield call(worker);
		} catch {
			// The worker is silent by contract; a throw must not end the loop and stop all refreshes.
		}
	}
}
